import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { fetchBranding } from '@/api/branding';
import { queryKeys } from '@/api/queryKeys';

import { accentIsUsable } from '@/pages/Settings/accentField';
import { hexToHslChannels } from './color';
import { whiteLabel } from './tokens';

/**
 * §2's white-label properties, applied — **the task Sprint 8 had to strike** (TASK-102, and TASK-057's
 * two struck criteria discharged).
 *
 * TASK-057 was written with a criterion requiring the shell to set §2's three properties from
 * `GET /api/v1/branding/`, and a second requiring an accent that fails contrast to be *refused rather
 * than rendered*. Neither was achievable in Sprint 8: the endpoint exposed `tenant.name` and nothing
 * else, so the frontend had nothing to apply and nothing to refuse. Both were **struck** on that task
 * rather than ticked (2026-07-27), with the reasoning beside them. Backend TASK-096 and TASK-098 gave
 * the endpoint an accent and a logo; this component is the other half.
 *
 * ⚠️ **It applies them once, in one place — above the router, so sign-in is included.** `/sign-in` is
 * outside `AppShell` (it has no session and no sidebar), and it is the one screen that renders before
 * anybody has signed in, which is precisely the screen a white-label brand exists for. Applying the
 * properties in the shell would have left it unbranded; applying them in *both* would be two writers of
 * the same three values, which is the second-source-of-truth this criterion forbids. Mounting here also
 * means a screen never reads `/branding/` in order to *style* itself — `AppShell` and `SignIn` read the
 * same query for the tenant's **name**, off the same cache entry, which is one source read twice rather
 * than two sources.
 *
 * ⚠️ **And mounting there is why the brand has to be refetched explicitly when the reader changes.**
 * `GET /api/v1/branding/` follows the session since BUG-045, while this holds it at
 * `staleTime: Infinity`; every screen escapes a stale brand by unmounting on the way out, and this
 * one does not unmount at all. `queryClient.clear()` is not enough — it *removes* the entry, which
 * orphans this observer rather than refreshing it. `readerChanged` (`app/queryClient.ts`) is what
 * sign-in, invitation redemption, sign-out and the 401 path call instead;
 * `app/brandFollowsSession.test.tsx` drives all four transitions in one client.
 *
 * ⚠️ **The properties are set on `document.documentElement`, not on a wrapper.** Radix renders dialogs,
 * dropdowns and selects into a **portal** at the end of `<body>`; a wrapper `<div>` carrying the custom
 * properties would style the page and leave every overlay with the product's default accent — a
 * difference nobody sees until a tenant's brand is not their own colour.
 *
 * ⚠️ **An accent that fails contrast renders the default and says nothing.** The stored value cannot
 * fail — the server refuses one (backend TASK-096) — so this is the second line, for a value that
 * arrives failing anyway: a hand-edited database, a release older than the rule, a future foreground
 * that changes the measurement. §2's sentence is *"an unreadable primary button is not a branding
 * preference"*, and the reader of a branded page is not the person who can fix it, so there is no
 * message: the product's own colour is the correct fallback and it is what they get.
 */
export function TenantBrand({ children }: { children: React.ReactNode }) {
  const branding = useQuery({
    queryKey: queryKeys.branding(),
    queryFn: fetchBranding,
    staleTime: Infinity,
  });

  const accent = branding.data?.tenant?.accent ?? null;
  const logoUrl = branding.data?.tenant?.logo_url ?? null;

  useEffect(() => {
    const root = document.documentElement;

    /*
     * `--accent` and `--accent-fg` are set **together or not at all**. They are a pair: a tenant
     * accent with the product's foreground is a combination nobody measured, and §2 measures the
     * button as well as the page. Today the foreground is always the product's white (the API does
     * not expose one yet), so setting it is a no-op — and writing it here is what makes the day it
     * becomes writable a one-line change rather than a hunt.
     */
    if (accent !== null && accentIsUsable(accent)) {
      root.style.setProperty('--accent', hexToHslChannels(accent));
      root.style.setProperty('--accent-fg', hexToHslChannels(whiteLabel.accentForeground));
    } else {
      // Removing rather than setting the default: `index.css` owns the product's own values, so
      // clearing the inline property hands the page back to the stylesheet instead of pinning a
      // second copy of the default here.
      root.style.removeProperty('--accent');
      root.style.removeProperty('--accent-fg');
    }

    if (logoUrl !== null) {
      // `url()` composed here rather than stored in the payload: the API returns a path, and a
      // custom property that already carried CSS syntax would be a place for a payload to inject it.
      root.style.setProperty('--logo', `url("${encodeURI(logoUrl)}")`);
    } else {
      root.style.removeProperty('--logo');
    }
  }, [accent, logoUrl]);

  return children;
}
